/**
 * SuccessStories — Testimonials with stats
 * Design: Navy background, white cards, green accents
 */

import React from "react";
import { Star, Quote } from "lucide-react";

const SUCCESS_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663400393649/gEFtNmSeGBDwQH79ZrNHXT/funding_success-JgqJZuPbYzP5vE72o2uqjR.webp";

const testimonials = [
  {
    name: "Marcus Johnson",
    title: "Owner, GreenLeaf Cafe",
    amount: "$250,000",
    loanType: "SBA Loan",
    loanColor: "#48d480",
    quote: "Legacy Triangle made the entire process seamless. We secured $250K to expand to 3 new locations. Their team handled everything — I just signed the papers!",
    avatar: "MJ",
    stars: 5,
    result: "Expanded from 1 to 4 locations",
  },
  {
    name: "Sarah Chen",
    title: "CEO, TechForward Solutions",
    amount: "$500,000",
    loanType: "Business Line of Credit",
    loanColor: "#e8821c",
    quote: "As a tech startup, getting funding was always a challenge. Legacy Triangle connected us with the right lenders and we got funded in just 5 days. Game changer!",
    avatar: "SC",
    stars: 5,
    result: "Hired 12 new employees",
  },
  {
    name: "Robert Williams",
    title: "Owner, Williams Construction",
    amount: "$1.2M",
    loanType: "Equipment Financing",
    loanColor: "#1a4a7a",
    quote: "We needed heavy equipment fast. Legacy Triangle got us $1.2M in equipment financing with better rates than our bank offered. Couldn't be happier.",
    avatar: "RW",
    stars: 5,
    result: "Doubled project capacity",
  },
];

const metrics = [
  { value: "300+", label: "5-Star Reviews" },
  { value: "$50M+", label: "Funding Distributed" },
  { value: "10,000+", label: "Businesses Funded" },
  { value: "24hrs", label: "Avg. Funding Speed" },
];

export default function SuccessStories() {
  return (
    <section id="success-stories" data-testid="success-stories" className="py-20 md:py-28 bg-[#0f2d52] relative overflow-hidden">
      {/* Background image overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url(${SUCCESS_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <div className="container relative z-10">
        {/* Header */}
        <div className="text-center mb-14 fade-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#48d480]" />
            <span className="section-label">Success Stories</span>
            <div className="h-px w-8 bg-[#48d480]" />
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-bold text-white"
          >
            Real Businesses, Real Results
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-white/70 mt-3 max-w-xl mx-auto"
          >
            Join thousands of business owners who have transformed their companies with Legacy Triangle funding.
          </p>
        </div>

        {/* Metrics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14 fade-up">
          {metrics.map((metric) => (
            <div key={metric.label} className="text-center bg-white/5 border border-white/10 rounded-xl py-5">
              <div
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl font-black text-[#48d480] mb-1"
              >
                {metric.value}
              </div>
              <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs font-medium">
                {metric.label}
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, idx) => (
            <div
              key={t.name}
              className="bg-white rounded-2xl p-7 shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 fade-up flex flex-col"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} size={16} className="text-[#e8821c] fill-[#e8821c]" />
                ))}
              </div>

              {/* Quote */}
              <div className="relative flex-1">
                <Quote size={28} className="text-[#48d480]/30 absolute -top-1 -left-1" />
                <p
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  className="text-gray-700 text-sm leading-relaxed pl-5 italic"
                >
                  {t.quote}
                </p>
              </div>

              {/* Result badge */}
              <div className="mt-4 mb-5 inline-flex items-center gap-2 bg-[#48d480]/10 rounded-full px-3 py-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#48d480]" />
                <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#1a7a4a] text-xs font-semibold">{t.result}</span>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: "#0f2d52", fontFamily: "'Inter', sans-serif" }}
                  >
                    {t.avatar}
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#0f2d52] font-bold text-sm">{t.name}</div>
                    <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-400 text-xs">{t.title}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-black text-base">{t.amount}</div>
                  <div
                    className="text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: t.loanColor + "20", color: t.loanColor, fontFamily: "'Inter', sans-serif" }}
                  >
                    {t.loanType}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
